import type { Priority, SourceRef, TargetType, WorkItemOp, WorkItemStatus, WorkItemSummary } from '../types/workflow.js';

export interface TreeNode {
  id: string;
  title: string;
  parentId: string | null;
  ordinal: number;
  type: TargetType;
  children: TreeNode[];
  deleted?: boolean;
}

export type DiffOp = 'renamed' | 'added' | 'deleted' | 'moved' | 'reordered' | 'rewired' | 'unchanged';

export interface TreeDiffEntry {
  workItemId: string;
  op: WorkItemOp;
  diffOp: DiffOp;
  targetId: string | null;
  targetType: TargetType;
  currentTitle?: string;
  proposedTitle?: string;
  currentParentId?: string;
  proposedParentId?: string;
  currentOrdinal?: number;
  proposedOrdinal?: number;
  rationale: string;
  sourceRefs: SourceRef[];
  priority: Priority;
  status: WorkItemStatus;
  clusterKey: string;
  confidence?: number;
  // Concept-map (Stream 1) edge changes. A work item whose patch carries `prerequisites`
  // is a rewire even when title/parent/ordinal are untouched — without these the change
  // would render as "unchanged" and silently no-op in review.
  prerequisitesChanged?: boolean;
  currentPrerequisites?: unknown;
  proposedPrerequisites?: unknown;
}

type PatchObject = Record<string, unknown>;

export function toTreeNodeId(targetType: TargetType, value: string | number) {
  return `${targetType}:${String(value)}`;
}

export function toSyntheticTreeNodeId(targetType: TargetType, workItemId: string) {
  return `${targetType}:new:${workItemId}`;
}

export function buildTreeDiff(currentTree: TreeNode[], structuralItems: WorkItemSummary[]): TreeDiffEntry[] {
  const currentIndex = new Map(flattenTree(currentTree).map((node) => [node.id, node] as const));

  return structuralItems.map((item) => {
    const patch = item.suggestedPatch ?? {};
    const targetId = resolveTargetId(item, patch);
    const current = targetId ? currentIndex.get(targetId) : undefined;
    const currentTitle = resolveCurrentTitle(patch, current);
    const proposedTitle = resolveProposedTitle(patch, current, item.op);
    const currentParentId = current?.parentId ?? null;
    const proposedParentId = resolveParentId(patch, current);
    const currentOrdinal = current?.ordinal;
    const proposedOrdinal = resolveOrdinal(patch, current);
    const prereq = readPrerequisitesPatch(patch);

    // A prerequisite-only change maps to the op `mapOpToDiffOp` would call 'unchanged';
    // surface it as 'rewired' so edge changes are visible and reviewable.
    const baseDiffOp = mapOpToDiffOp(item.op);
    const diffOp: DiffOp = baseDiffOp === 'unchanged' && prereq.changed ? 'rewired' : baseDiffOp;

    return {
      workItemId: item.workItemId,
      op: item.op,
      diffOp,
      targetId,
      targetType: item.targetType,
      ...(currentTitle ? { currentTitle } : {}),
      ...(proposedTitle ? { proposedTitle } : {}),
      ...(currentParentId !== null ? { currentParentId } : {}),
      ...(proposedParentId !== null ? { proposedParentId } : {}),
      ...(currentOrdinal !== undefined ? { currentOrdinal } : {}),
      ...(proposedOrdinal !== undefined ? { proposedOrdinal } : {}),
      rationale: item.rationale,
      sourceRefs: item.sourceRefs,
      priority: item.priority,
      status: item.status,
      clusterKey: item.clusterKey,
      ...(item.confidence !== undefined ? { confidence: item.confidence } : {}),
      ...(prereq.changed ? { prerequisitesChanged: true } : {}),
      ...(prereq.before !== undefined ? { currentPrerequisites: prereq.before } : {}),
      ...(prereq.after !== undefined ? { proposedPrerequisites: prereq.after } : {}),
    };
  });
}

/**
 * Reads a `prerequisites` change out of a work-item patch. Supports both the
 * `{ before, after }` envelope used by other structural fields and a raw value
 * (treated as the proposed/after state). The edge shape itself is opaque here.
 */
function readPrerequisitesPatch(patch: PatchObject): { changed: boolean; before?: unknown; after?: unknown } {
  const raw = patch.prerequisites;
  if (raw === undefined) {
    return { changed: false };
  }
  if (raw && typeof raw === 'object' && !Array.isArray(raw) && ('before' in raw || 'after' in raw)) {
    const envelope = raw as Record<string, unknown>;
    return { changed: true, before: envelope.before, after: envelope.after };
  }
  return { changed: true, after: raw };
}

export function buildProposedTree(currentTree: TreeNode[], diffs: TreeDiffEntry[]): TreeNode[] {
  const tree = cloneTree(currentTree);
  const index = new Map(flattenTree(tree).map((node) => [node.id, node] as const));
  const additions = diffs.filter((diff) => diff.diffOp === 'added' && diff.targetId);

  for (const diff of diffs) {
    if (diff.diffOp === 'added') {
      continue;
    }

    if (!diff.targetId) {
      continue;
    }

    const node = index.get(diff.targetId);
    if (!node) {
      continue;
    }

    if (diff.proposedTitle) {
      node.title = diff.proposedTitle;
    }

    if (diff.diffOp === 'deleted') {
      markDeletedSubtree(node);
    }

    if (diff.diffOp === 'moved' && diff.proposedParentId !== undefined && diff.proposedParentId !== node.parentId) {
      moveNode(tree, index, node.id, diff.proposedParentId);
    }

    if ((diff.diffOp === 'moved' || diff.diffOp === 'reordered') && diff.proposedOrdinal !== undefined) {
      node.ordinal = diff.proposedOrdinal;
    }
  }

  insertAddedNodes(tree, index, additions);

  return sortTree(tree);
}

export function mapOpToDiffOp(op: WorkItemOp): DiffOp {
  if (op === 'rename') return 'renamed';
  if (op === 'create') return 'added';
  if (op === 'archive') return 'deleted';
  if (op === 'move') return 'moved';
  if (op === 'reorder') return 'reordered';
  return 'unchanged';
}

function resolveTargetId(item: WorkItemSummary, patch: PatchObject): string | null {
  const explicitPatchId = readString(patch.id) ?? readString(patch.targetId);
  if (explicitPatchId) {
    return normalizeNodeId(item.targetType, explicitPatchId);
  }
  if (item.targetId) {
    return normalizeNodeId(item.targetType, item.targetId);
  }
  if (item.op === 'create') {
    return toSyntheticTreeNodeId(item.targetType, item.workItemId);
  }
  return null;
}

function resolveCurrentTitle(patch: PatchObject, current?: TreeNode): string | undefined {
  return readPatchValue(patch.title, 'before') ?? current?.title;
}

function resolveProposedTitle(patch: PatchObject, current: TreeNode | undefined, op: WorkItemOp): string | undefined {
  const next = readPatchValue(patch.title, 'after') ?? readString(patch.name) ?? readPatchValue(patch.name, 'after');
  if (next) {
    return next;
  }
  if (op === 'create') {
    return current?.title ?? 'New node';
  }
  return current?.title;
}

function resolveParentId(patch: PatchObject, current?: TreeNode): string | null {
  const next =
    readPatchValue(patch.parentId, 'after') ??
    readPatchValue(patch.parent_id, 'after') ??
    readString(patch.parentId) ??
    readString(patch.parent_id) ??
    readString(patch.groupId) ??
    readString(patch.group_id);
  return next ? normalizeNodeId('group', next) : current?.parentId ?? null;
}

function resolveOrdinal(patch: PatchObject, current?: TreeNode): number | undefined {
  return (
    readNumberPatchValue(patch.ordinal, 'after') ??
    readNumberPatchValue(patch.position, 'after') ??
    readNumber(patch.ordinal) ??
    readNumber(patch.position) ??
    current?.ordinal
  );
}

function readPatchValue(value: unknown, key: 'before' | 'after'): string | undefined {
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'object' && value && !Array.isArray(value)) {
    const candidate = (value as Record<string, unknown>)[key];
    return typeof candidate === 'string' && candidate.trim() ? candidate : undefined;
  }
  return undefined;
}

function readNumberPatchValue(value: unknown, key: 'before' | 'after'): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'object' && value && !Array.isArray(value)) {
    return readNumber((value as Record<string, unknown>)[key]);
  }
  return undefined;
}

function readString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function readNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function normalizeNodeId(targetType: TargetType, value: string) {
  if (value.includes(':')) {
    if (value.startsWith('new:')) {
      return `${targetType}:${value}`;
    }
    return value;
  }
  return toTreeNodeId(targetType, value);
}

function flattenTree(nodes: TreeNode[]): TreeNode[] {
  return nodes.flatMap((node) => [node, ...flattenTree(node.children)]);
}

function cloneTree(nodes: TreeNode[]): TreeNode[] {
  return nodes.map((node) => ({
    ...node,
    children: cloneTree(node.children),
  }));
}

function markDeletedSubtree(node: TreeNode) {
  node.deleted = true;
  node.children.forEach(markDeletedSubtree);
}

function sortTree(nodes: TreeNode[]): TreeNode[] {
  return [...nodes]
    .sort((left, right) => left.ordinal - right.ordinal || left.title.localeCompare(right.title))
    .map((node) => ({ ...node, children: sortTree(node.children) }));
}

function insertNode(tree: TreeNode[], index: Map<string, TreeNode>, node: TreeNode, parentId: string | null) {
  if (parentId) {
    const parent = index.get(parentId);
    if (parent) {
      parent.children.push(node);
      index.set(node.id, node);
      return;
    }
  }
  tree.push(node);
  index.set(node.id, node);
}

function insertAddedNodes(tree: TreeNode[], index: Map<string, TreeNode>, diffs: TreeDiffEntry[]) {
  let pending = diffs.map((diff) => ({ ...diff }));

  while (pending.length > 0) {
    const nextPending: TreeDiffEntry[] = [];
    let progressed = false;

    for (const diff of pending) {
      if (!diff.targetId) {
        continue;
      }

      const parentId = diff.proposedParentId ?? null;
      if (parentId && !index.has(parentId)) {
        nextPending.push(diff);
        continue;
      }

      const node: TreeNode = {
        id: diff.targetId,
        title: diff.proposedTitle ?? 'New node',
        parentId,
        ordinal: diff.proposedOrdinal ?? nextOrdinal(parentId, tree, index),
        type: diff.targetType,
        children: [],
      };
      insertNode(tree, index, node, parentId);
      progressed = true;
    }

    if (!nextPending.length) {
      break;
    }

    if (!progressed) {
      nextPending.forEach((diff) => {
        if (!diff.targetId) {
          return;
        }
        const node: TreeNode = {
          id: diff.targetId,
          title: diff.proposedTitle ?? 'New node',
          parentId: diff.proposedParentId ?? null,
          ordinal: diff.proposedOrdinal ?? nextOrdinal(null, tree, index),
          type: diff.targetType,
          children: [],
        };
        insertNode(tree, index, node, null);
      });
      break;
    }

    pending = nextPending;
  }
}

function moveNode(tree: TreeNode[], index: Map<string, TreeNode>, targetId: string, nextParentId?: string | null) {
  const node = index.get(targetId);
  if (!node) {
    return;
  }
  detachNode(tree, targetId);
  node.parentId = nextParentId ?? null;
  insertNode(tree, index, node, nextParentId ?? null);
}

function detachNode(nodes: TreeNode[], targetId: string): TreeNode | null {
  for (let index = 0; index < nodes.length; index += 1) {
    const current = nodes[index];
    if (!current) {
      continue;
    }
    if (current?.id === targetId) {
      const [removed] = nodes.splice(index, 1);
      return removed ?? null;
    }
    const child = detachNode(current.children, targetId);
    if (child) {
      return child;
    }
  }
  return null;
}

function nextOrdinal(parentId: string | null, tree: TreeNode[], index: Map<string, TreeNode>) {
  if (!parentId) {
    return tree.length + 1;
  }
  return (index.get(parentId)?.children.length ?? 0) + 1;
}
